import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { clarityEvent, gaEvent } from '../analytics';
import { submitNewsletterSubscribe, submitWaitlistJoin } from '../services/contactService';
import styles from './Footer.module.css';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const RECAPTCHA_ENABLED = Boolean(RECAPTCHA_SITE_KEY);

export default function Footer({ variant = 'default' }) {
  const isInceptum = variant === 'inceptum';
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [recaptchaError, setRecaptchaError] = useState('');
  const [showRecaptcha, setShowRecaptcha] = useState(false);
  const [popup, setPopup] = useState({ isOpen: false, phase: 'loading', message: '' });
  const recaptchaRef = useRef(null);
  const popupButtonRef = useRef(null);
  const previousFocusRef = useRef(null);

  const isRecaptchaTimeout = useCallback((err) => {
    const msg = err?.message || err?.toString?.() || '';
    return typeof msg === 'string' && msg.toLowerCase().includes('timeout');
  }, []);

  const safeResetRecaptcha = useCallback((context) => {
    try {
      recaptchaRef.current?.reset?.();
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.debug('Ignoring footer reCAPTCHA reset error', context, err);
      }
    }
    setRecaptchaToken('');
  }, []);

  useEffect(() => {
    return () => {
      safeResetRecaptcha('cleanup');
      setRecaptchaToken('');
    };
  }, [safeResetRecaptcha]);

  // Focus trap + Escape key for popup
  useEffect(() => {
    if (popup.isOpen) {
      previousFocusRef.current = document.activeElement;
      const timer = setTimeout(() => popupButtonRef.current?.focus(), 80);
      const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
          handlePopupOk();
        }
        if (e.key === 'Tab') {
          // trap focus inside popup
          e.preventDefault();
          popupButtonRef.current?.focus();
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        clearTimeout(timer);
        document.removeEventListener('keydown', handleKeyDown);
      };
    } else if (previousFocusRef.current) {
      previousFocusRef.current?.focus?.();
      previousFocusRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popup.isOpen]);

  const trackFooterLink = (label, href) => {
    gaEvent('click_footer_link', { link_label: label, href });
    clarityEvent('click_footer_link');
  };

  const handleFooterLogoClick = () => {
    gaEvent('click_studio_logo', { href: '/' });
    clarityEvent('click_studio_logo');
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setRecaptchaError('');

    const analyticsId = isInceptum ? 'footer_waitlist' : 'footer_newsletter';
    if (isInceptum) {
      gaEvent('waitlist_submit_attempt', { form_name: analyticsId });
      clarityEvent('waitlist_submit_attempt');
    }

    const normalizedEmail = email.trim().toLowerCase();
    if (!EMAIL_REGEX.test(normalizedEmail)) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    if (!RECAPTCHA_ENABLED) {
      setRecaptchaError('reCAPTCHA is not configured.');
      gaEvent('recaptcha_error', { reason: `${analyticsId}_not_configured` });
      clarityEvent('recaptcha_error');
      return;
    }

    if (!recaptchaToken) {
      setShowRecaptcha(true);
      setRecaptchaError('Please complete the reCAPTCHA.');
      gaEvent('recaptcha_error', { reason: `${analyticsId}_missing_token` });
      clarityEvent('recaptcha_error');
      return;
    }

    try {
      setStatus('submitting');
      setPopup({
        isOpen: true,
        phase: 'loading',
        message: isInceptum ? 'Joining the waitlist\u2026' : 'Subscribing\u2026',
      });

      if (isInceptum) {
        await submitWaitlistJoin({
          email: normalizedEmail,
          recaptchaToken,
          newsletterOptIn: false,
        });
      } else {
        await submitNewsletterSubscribe({ email: normalizedEmail, recaptchaToken });
      }

      gaEvent('generate_lead', {
        page_section: analyticsId,
        ...(isInceptum ? { newsletter_opt_in: false } : {}),
      });
      clarityEvent(`${analyticsId}_success`);

      setStatus('success');
      setPopup({
        isOpen: true,
        phase: 'success',
        message: isInceptum
          ? "We\u2019ll let you know when preorders open and send your exclusive discount."
          : "We\u2019ll keep you updated as we get closer to launch.",
      });
      setEmail('');
      setRecaptchaError('');
      safeResetRecaptcha(`${analyticsId}-success`);
    } catch (err) {
      setStatus('error');
      setPopup({
        isOpen: true,
        phase: 'error',
        message: err?.message || (isInceptum
          ? 'Could not join the waitlist right now. Please try again.'
          : 'Could not subscribe right now. Please try again.'),
      });
      gaEvent('form_error', { form_name: analyticsId });
      clarityEvent(`${analyticsId}_error`);
    }
  };

  const handlePopupOk = () => {
    setPopup({ isOpen: false, phase: 'loading', message: '' });
    setStatus('idle');
    setRecaptchaError('');
    setShowRecaptcha(false);
    safeResetRecaptcha('popup-ok');
  };

  const renderPopupMessage = (message) => {
    const lines = message.split('\n');
    return lines.map((line, index) => (
      <React.Fragment key={`${line}-${index}`}>
        {line}
        {index < lines.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  return (
    <footer
      className={`${styles.footer} ${isInceptum ? styles.footerInceptum : ''}`}
    >
      <div className={styles.quickLinksSection}>
        <div className={styles.quickLinksInner}>
          <div className={styles.footerBrand}>
            <Link
              to="/"
              className={styles.footerLogoLink}
              onClick={handleFooterLogoClick}
            >
              <img
                src="/images/webp/logo.webp"
                srcSet="/images/webp/logo_192w.webp 192w, /images/webp/logo.webp 357w"
                sizes="153px"
                alt="StratMech Studios"
                className={styles.footerLogo}
                width={357}
                height={245}
              />
            </Link>
          </div>

          {/* Footer signup */}
          <div className={styles.newsletterSection}>
            <h3 className={styles.newsletterHeading}>
              {isInceptum ? 'Join the INCEPTUM waitlist' : 'Stay in the loop'}
            </h3>
            <p className={styles.newsletterSubtext}>
              {isInceptum
                ? 'Get the exclusive preorder discount when the first print run opens.'
                : 'Subscribe for updates on releases, development notes, and more.'}
            </p>
            <form className={styles.newsletterForm} onSubmit={handleSignupSubmit}>
              <input
                type="email"
                value={email}
                onFocus={() => setShowRecaptcha(true)}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errorMessage) setErrorMessage('');
                }}
                placeholder="Your email address"
                className={styles.newsletterInput}
                required
                aria-label={isInceptum ? 'Email address for INCEPTUM waitlist' : 'Email address for newsletter'}
              />
              <button
                type="submit"
                className={styles.newsletterButton}
                disabled={status === 'submitting' || !RECAPTCHA_ENABLED}
              >
                {isInceptum ? 'Get the waitlist offer' : 'Subscribe'}
              </button>
            </form>
            {errorMessage && <p className={styles.newsletterError}>{errorMessage}</p>}
            {RECAPTCHA_ENABLED && showRecaptcha ? (
              <div className={styles.newsletterRecaptcha}>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={(token) => {
                    setRecaptchaToken(token || '');
                    if (recaptchaError) setRecaptchaError('');
                  }}
                  onExpired={() => {
                    setRecaptchaToken('');
                    setRecaptchaError('reCAPTCHA expired. Please retry.');
                    gaEvent('recaptcha_error', { reason: 'footer_expired' });
                    clarityEvent('recaptcha_error');
                  }}
                  onErrored={(err) => {
                    setRecaptchaToken('');
                    setRecaptchaError(
                      isRecaptchaTimeout(err)
                        ? 'reCAPTCHA timed out. Please retry.'
                        : 'reCAPTCHA failed to load. Please retry.'
                    );
                    gaEvent('recaptcha_error', { reason: isRecaptchaTimeout(err) ? 'footer_timeout' : 'footer_load_failed' });
                    clarityEvent('recaptcha_error');
                  }}
                  theme={isInceptum ? 'dark' : 'light'}
                />
              </div>
            ) : !RECAPTCHA_ENABLED ? (
              <p className={styles.newsletterError}>reCAPTCHA is unavailable right now.</p>
            ) : null}
            {recaptchaError && <p className={styles.newsletterError}>{recaptchaError}</p>}
          </div>

          <div className={styles.quickLinksGrid}>
            <div className={styles.quickLinksColumnGroup}>
              <h3 className={styles.columnHeading}>Connect</h3>
              <ul className={styles.linkList}>
                <li><a href="/#partner" onClick={() => trackFooterLink('Partner', '/#partner')}>Partner</a></li>
                <li><Link to="/contact" onClick={() => trackFooterLink('Contact', '/contact')}>Contact</Link></li>
                <li><a href="https://www.instagram.com/stratmechstudios/" target="_blank" rel="noopener noreferrer" onClick={() => trackFooterLink('Instagram', 'https://www.instagram.com/stratmechstudios/')}>Follow us on Instagram</a></li>
              </ul>
            </div>
            <div className={styles.quickLinksColumnGroup}>
              <h3 className={styles.columnHeading}>Orders</h3>
              <ul className={styles.linkList}>
                <li><Link to="/shipping" onClick={() => trackFooterLink('Shipping', '/shipping')}>Shipping</Link></li>
                <li><Link to="/refund-cancellation-policy" onClick={() => trackFooterLink('Refund & Cancellation', '/refund-cancellation-policy')}>Refund &amp; Cancellation</Link></li>
              </ul>
            </div>
            <div className={styles.quickLinksColumnGroup}>
              <h3 className={styles.columnHeading}>Legal</h3>
              <ul className={styles.linkList}>
                <li><Link to="/privacy-policy" onClick={() => trackFooterLink('Privacy Policy', '/privacy-policy')}>Privacy Policy</Link></li>
                <li><Link to="/ip-policy" onClick={() => trackFooterLink('IP Policy', '/ip-policy')}>IP Policy</Link></li>
                <li><Link to="/terms-of-service" onClick={() => trackFooterLink('Terms of Service', '/terms-of-service')}>Terms of Service</Link></li>
              </ul>
            </div>
          </div>

          {/* Contact line */}
          <div className={styles.contactLine}>
            <p>
              If you have a question, an idea, or feedback to share, we&apos;d love to hear from you.
              {' '}Please reach out to us at{' '}
              <a
                href="mailto:info@stratmechstudios.com"
                className={styles.contactEmail}
                onClick={() => {
                  gaEvent('click_contact', { href: 'mailto:info@stratmechstudios.com' });
                  clarityEvent('click_contact');
                }}
              >
                info@stratmechstudios.com
              </a>.
            </p>
          </div>

          <div className={styles.footerBottom}>
            <p>&copy; 2026 StratMech Studios. All rights reserved.</p>
          </div>
        </div>
      </div>

      {/* Signup success/error popup */}
      {popup.isOpen && (
        <div className={`${styles.popupOverlay} ${isInceptum ? styles.popupOverlayInceptum : ''}`} role="dialog" aria-modal="true" aria-live="polite">
          <div className={`${styles.popupCard} ${isInceptum ? styles.popupCardInceptum : ''}`}>
            {popup.phase === 'loading' ? (
              <>
                <div className={`${styles.popupSpinner} ${isInceptum ? styles.popupSpinnerInceptum : ''}`} />
                <h3 className={`${styles.popupTitle} ${isInceptum ? styles.popupTitleInceptum : ''}`}>Please wait</h3>
                <p className={`${styles.popupMessage} ${isInceptum ? styles.popupMessageInceptum : ''}`}>{popup.message}</p>
              </>
            ) : (
              <>
                {popup.phase === 'success' && (
                  <div className={`${styles.popupSuccessIcon} ${isInceptum ? styles.popupSuccessIconInceptum : ''}`}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                )}
                <h3 className={`${styles.popupTitle} ${isInceptum ? styles.popupTitleInceptum : ''}`}>
                  {popup.phase === 'success'
                    ? (isInceptum ? "You\u2019re on the waitlist" : "You\u2019re subscribed")
                    : 'Something went wrong'}
                </h3>
                <p className={`${styles.popupMessage} ${isInceptum ? styles.popupMessageInceptum : ''}`}>
                  {popup.message}
                </p>
                <button
                  ref={popupButtonRef}
                  type="button"
                  className={`${styles.popupButton} ${isInceptum ? styles.popupButtonInceptum : ''}`}
                  onClick={handlePopupOk}
                >
                  Got it
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </footer>
  );
}
